import { ZERO_WEI } from '@kwenta/sdk/constants'
import { PerpsProvider, PositionSide } from '@kwenta/sdk/types'
import { formatDollars, formatNumber, isZero, toWei } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'

import { type MouseEventHandler, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { ConditionOrderTableItemCross, ConditionOrderTableItemIsolated } from 'types/futures'

import { StyledBaseModal } from 'components/BaseModal'
import { TransactionButton } from 'components/Button/Button'
import { InfoBoxRow } from 'components/InfoBox'
import PreviewArrow from 'components/PreviewArrow'
import Spacer from 'components/Spacer'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import { setShowConditionalOrderModal, setTransaction } from 'state/app/reducer'
import { handleOneClickOperation } from 'state/futures/actions'
import {
	selectEditCOModalInputs,
	selectLeverageSide,
	selectMarketIndexPrice,
	selectPerpsProvider,
} from 'state/futures/common/selectors'
import { updateIsolatedConditionalOrder } from 'state/futures/isolatedMargin/actions'
import { selectIsFetchingTradePreviewSmartMargin } from 'state/futures/isolatedMargin/selectors'
import {
	selectAvailableMargin,
	selectAvailableOi,
	selectEditPositionPreview,
	selectEditSLTPModalInfo,
	selectMaxLeverage,
	selectSubmittingFuturesTx,
} from 'state/futures/selectors'
import { updateV3ConditionalOrder } from 'state/futures/snxPerpsV3/actions'
import { selectMaxUsdSizeInputCrossMargin } from 'state/futures/snxPerpsV3/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import styled from 'styled-components'
import {
	providerIsCrossMargin,
	unserializeConditionOrderTableItem,
	unserializeConditionOrderTableItemCross,
	unserializeConditionOrderTableItemIsolated,
} from 'utils/futures'

import EditPositionFeeInfo from '../FeeInfoBox/EditPositionFeeInfo'
import PositionType from '../PositionType'

import EditOrderMarginInput from './EditOrderMarginInput'
import EditOrderPriceInput from './EditOrderPriceInput'
import EditOrderSizeInput from './EditOrderSizeInput'

export default function EditConditionalOrderModal() {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()

	const futuresType = useAppSelector(selectPerpsProvider)
	const orderInfo = useAppSelector(selectEditSLTPModalInfo)
	const isSubmitting = useAppSelector(selectSubmittingFuturesTx)
	const isFetchingPreview = useAppSelector(selectIsFetchingTradePreviewSmartMargin)
	const preview = useAppSelector(selectEditPositionPreview)
	const { orderPrice, size, margin } = useAppSelector(selectEditCOModalInputs)
	const availableMargin = useAppSelector(selectAvailableMargin)

	const order = orderInfo?.order
	const orderType = order?.orderTypeDisplay

	useEffect(() => {
		dispatch(setTransaction(undefined))
	}, [dispatch])

	const isLoading = useMemo(
		() => isSubmitting || isFetchingPreview,
		[isSubmitting, isFetchingPreview]
	)

	const marketIndexPrice = useAppSelector(selectMarketIndexPrice)

	const maxCMUsdSize = useAppSelector(selectMaxUsdSizeInputCrossMargin)
	const maxLeverage = useAppSelector(selectMaxLeverage)
	const maxUsdInputAmount = useMemo(() => {
		if (!providerIsCrossMargin(futuresType)) {
			return order?.marginDelta && wei(order?.marginDelta).gt(0)
				? toWei(maxLeverage).mul(order.marginDelta)
				: toWei(maxLeverage).mul(availableMargin)
		}
		return wei(maxCMUsdSize)
	}, [futuresType, order, maxCMUsdSize, maxLeverage, availableMargin])

	const tradeSide = useAppSelector(selectLeverageSide)
	const availableOi = useAppSelector(selectAvailableOi)

	const tradePrice = useMemo(
		() => (orderPrice.price ? wei(orderPrice.price) : wei(marketIndexPrice)),
		[orderPrice, marketIndexPrice]
	)

	const increasingOrder = useMemo(
		() => size && wei(size).gt(wei(order?.size ?? 0).abs()),
		[size, order?.size]
	)

	const unserializedOrder = useMemo(() => {
		if (order) {
			return unserializeConditionOrderTableItem(order as ConditionOrderTableItemIsolated<string>)
		}
		return undefined
	}, [order])

	const availableOiNative = useMemo(() => {
		return increasingOrder
			? wei(availableOi[tradeSide].native)
			: wei(availableOi[tradeSide].native).add(order?.size || 0)
	}, [tradeSide, availableOi, increasingOrder, order?.size])

	const maxNativeValue = useMemo(() => {
		const max = !isZero(tradePrice) ? maxUsdInputAmount.div(tradePrice) : ZERO_WEI
		return max.lt(availableOiNative) ? max : availableOiNative
	}, [tradePrice, maxUsdInputAmount, availableOiNative])

	const sizeWei = useMemo(() => (!size || Number.isNaN(Number(size)) ? wei(0) : wei(size)), [size])

	const invalidSize = useMemo(() => {
		return sizeWei.abs().gt(maxNativeValue.abs())
	}, [sizeWei, maxNativeValue])

	const submitDisabled = useMemo(() => {
		if (!orderPrice.price || Number(orderPrice.price) === 0) {
			return true
		}

		return sizeWei.eq(0) || invalidSize || !!orderPrice.invalidLabel || isLoading
	}, [sizeWei, invalidSize, isLoading, orderPrice])

	const onClose = () => {
		dispatch(setShowConditionalOrderModal(null))
	}

	const onClick: MouseEventHandler<HTMLButtonElement> = useCallback(() => {
		if (!order) return

		if (!providerIsCrossMargin(order.provider)) {
			const isolatedOrder = unserializeConditionOrderTableItemIsolated(
				order as ConditionOrderTableItemIsolated<string>
			)
			dispatch(
				handleOneClickOperation(() =>
					dispatch(
						updateIsolatedConditionalOrder({
							marketAddress: isolatedOrder.marketAddress,
							order: isolatedOrder,
							sizeDelta: order.side === PositionSide.LONG ? sizeWei : sizeWei.neg(),
							targetPrice: wei(orderPrice.price ?? 0),
							marginDelta: wei(margin),
							desiredFillPrice: wei(preview?.desiredFillPrice || 0),
						})
					)
				)
			)
		} else {
			const crossOrder = unserializeConditionOrderTableItemCross(
				order as ConditionOrderTableItemCross<string>
			)
			dispatch(
				updateV3ConditionalOrder({
					order: crossOrder,
					sizeDelta: sizeWei,
					targetPrice: wei(orderPrice.price ?? 0),
				})
			)
		}
	}, [dispatch, sizeWei, orderPrice.price, order, margin, preview?.desiredFillPrice])

	return (
		<StyledBaseModal
			title={
				<FlexDivCol>
					<Body size="large">{`Edit ${orderType}`}</Body>
					<FlexDivRowCentered justifyContent="left">
						<Body weight="bold" color="secondary">
							{order?.marketName}
						</Body>
						<Spacer width={5} />
						{order?.side && <PositionType side={order.side} />}
					</FlexDivRowCentered>
				</FlexDivCol>
			}
			isOpen
			onDismiss={onClose}
		>
			<Divider height={1} divider />
			{unserializedOrder && (
				<>
					{futuresType === PerpsProvider.SNX_V2_OP && unserializedOrder.marginDelta.gt(0) && (
						<EditOrderMarginInput order={unserializedOrder} />
					)}
					<EditOrderSizeInput order={unserializedOrder} maxNativeValue={maxNativeValue} />
					<EditOrderPriceInput order={unserializedOrder} />
				</>
			)}
			<Spacer height={20} />
			<InfoBoxRow
				textValueIcon={
					size && (
						<PreviewArrow showPreview>
							{size ? formatNumber(sizeWei.abs(), { suggestDecimals: true }) : '-'}
						</PreviewArrow>
					)
				}
				title={t('futures.market.trade.edit-conditional-order.size')}
				textValue={formatNumber(wei(order?.size ?? 0).abs(), { suggestDecimals: true })}
			/>
			<InfoBoxRow
				textValueIcon={
					tradePrice && (
						<PreviewArrow showPreview>
							{formatDollars(tradePrice, { suggestDecimals: true })}
						</PreviewArrow>
					)
				}
				title={t('futures.market.trade.edit-conditional-order.price')}
				textValue={formatDollars(order?.targetPrice || 0)}
			/>
			{futuresType === PerpsProvider.SNX_V2_OP && (
				<InfoBoxRow
					textValueIcon={
						margin && (
							<PreviewArrow showPreview>
								{formatDollars(margin, { suggestDecimals: true })}
							</PreviewArrow>
						)
					}
					title={t('futures.market.trade.edit-conditional-order.margin')}
					textValue={formatDollars(order?.marginDelta || 0, { suggestDecimals: true })}
				/>
			)}
			<Spacer height={10} />
			<TransactionButton
				loading={isLoading}
				variant="solid-highlight"
				data-testid="futures-market-trade-deposit-margin-button"
				disabled={submitDisabled}
				fullWidth
				onClick={onClick}
			>
				{t('futures.market.trade.edit-conditional-order.confirm')}
			</TransactionButton>
			<Spacer height={20} />
			<EditPositionFeeInfo />
		</StyledBaseModal>
	)
}

const Divider = styled(Spacer)`
	width: 100%;
`
